import { Component, type ErrorInfo, type ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { Button } from '@/components/base'

type ErrorBoundaryProps = {
  children: ReactNode
  fallback?: (error: Error, retry: () => void) => ReactNode
}

type ErrorBoundaryState = {
  error: Error | null
}

/**
 * Generic error boundary. Wrap subtrees that may throw — most relevant for
 * `<Suspense>` consumers where a chunk-load failure manifests as a thrown
 * promise rejection that Suspense itself does not recover from.
 *
 * Usage: place once at the app root for catch-all behaviour, and optionally
 * around a specific lazy-loaded section if you want a localised fallback.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  retry = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.retry)
    return <DefaultErrorFallback error={error} onRetry={this.retry} />
  }
}

export function DefaultErrorFallback({
  error,
  onRetry,
}: {
  error: Error
  onRetry: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-6">
      <p className="text-base font-semibold text-foreground">
        {COMMON.errors.renderFailed}
      </p>
      <p className="max-w-md text-center text-sm text-muted-foreground">
        {error.message || COMMON.errors.unexpected}
      </p>
      <Button variant="outline" className="mt-3" onClick={onRetry}>
        {COMMON.retry}
      </Button>
    </div>
  )
}
